const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

export interface ImageLog {
  id: string
  timestamp: string
  languagePair: string
  codeElement: string
  concept: string
  context: {
    surface: string
    concept: string
    paradigm: string
  }
  prompt: string
  model: string
  responseTime: number
  imageUrl?: string
  imageType?: string
  imageFile?: string
}

export interface GenerationRequest {
  languagePair: string
  codeElement: string
  concept: string
  context: {
    surface: string
    concept: string
    paradigm: string
  }
  prompt: string
  model?: string
}

export interface GenerationResult {
  imageUrl: string
  responseTime: number
  fileSize: number
  model: string
  textResponse?: string
}

class ImageLabService {
  /**
   * Get all image generation logs from backend
   */
  async getLogs(): Promise<ImageLog[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/image-lab/logs`)
      if (!response.ok) {
        throw new Error(`Failed to fetch logs: ${response.statusText}`)
      }
      const data = await response.json()
      return data.logs || []
    } catch (error) {
      console.error('Error fetching image logs:', error)
      throw error
    }
  }

  /**
   * Generate a new diagram image
   */
  async generateImage(request: GenerationRequest): Promise<GenerationResult> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/image-lab/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(error.error || 'Failed to generate image')
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error generating image:', error)
      throw error
    }
  }

  /**
   * Parse log files from backend/prompts/logs directory
   */
  parseLogMarkdown(markdown: string): Partial<ImageLog> {
    const lines = markdown.split('\n')
    const log: Partial<ImageLog> = {}

    // Extract metadata from frontmatter
    let inContext = false
    let currentContext = ''

    for (const line of lines) {
      if (line.startsWith('**Timestamp:**')) {
        log.timestamp = line.replace('**Timestamp:**', '').trim()
      } else if (line.startsWith('**Model:**')) {
        log.model = line.replace('**Model:**', '').trim()
      } else if (line.startsWith('**Concept:**')) {
        log.concept = line.replace('**Concept:**', '').trim()
      } else if (line.startsWith('**Response Time:**')) {
        const time = line.replace('**Response Time:**', '').replace('ms', '').trim()
        log.responseTime = parseInt(time, 10)
      } else if (line.startsWith('**Image File:**')) {
        log.imageFile = line.replace('**Image File:**', '').replace(/`/g, '').trim()
      } else if (line.startsWith('**Surface (Syntax Comparison):**')) {
        currentContext = 'surface'
        inContext = true
      } else if (line.startsWith('**Concept (Semantic Meaning):**')) {
        currentContext = 'concept'
        inContext = true
      } else if (line.startsWith('**Paradigm (Design Philosophy):**')) {
        currentContext = 'paradigm'
        inContext = true
      } else if (line.startsWith('##') || line.startsWith('**')) {
        inContext = false
      } else if (inContext && line.trim()) {
        if (!log.context) {
          log.context = { surface: '', concept: '', paradigm: '' }
        }
        log.context[currentContext as keyof typeof log.context] += line.trim() + ' '
      }
    }

    return log
  }
}

export const imageLabService = new ImageLabService()
